import { ComponentChildren, Fragment, VNode } from "preact"
import { useLayoutEffect, useState } from "preact/compat"
import { Redirect, Route, Switch, useLocation } from "wouter-preact"

import { AccountingChecks } from "@/accounting/accounting_checks.tsx"
import { CheckDetail } from "@/accounting/check_detail.tsx"
import { CashApplicationConfirmPage } from "@/accounting/CashApplicationConfirmPage.tsx"
import { ShortPaysPage } from "@/accounting/ShortPaysPage.tsx"
import { FillRatePage } from "@/fill_rate/FillRatePage.tsx"
import { SpoilagePage } from "@/spoilage/SpoilagePage.tsx"
import { DeliveriesPage } from "@/deliveries/DeliveriesPage.tsx"
import { PurchaseOrdersPage } from "@/purchase_orders/PurchaseOrdersPage.tsx"
import { OpenDeductionsPage } from "@/accounting/OpenDeductionsPage.tsx"
import { NetsuiteDocumentPage } from "@/accounting/NetsuiteDocumentPage.tsx"
import { CreditMemoDetailPage } from "@/accounting/CreditMemoDetailPage.tsx"
import { NewCheck } from "@/accounting/new_check.tsx"
import { GatedContainer } from "@/app/gated_container.tsx"
import { Sales } from "@/dashboard/sales"
import { TradePromotionManagement } from "@/dashboard/trade_promotion_management"
import { AccrualBankPage } from "@/dashboard/trade_promotion_management/accrual_bank/accrual_bank_page"
import { TradeReviewPage } from "@/dashboard/trade_promotion_management/trade_review/trade_review_page"
import { PromoLiftReviewPage } from "@/dashboard/trade_promotion_management/promo_lift_review/promo_lift_review_page"
import { CreatePromotionPage } from "@/dashboard/trade_promotion_management/promo_creation/create_promotion_page"
import { SalesForecasting } from "@/dashboard/sales_forecasting"
import { ForecastMappingsPage } from "@/dashboard/sales_forecasting/forecast_mappings"
import { SalesAnalysis } from "@/dashboard/sales_analysis"
import { RetailerMappingsPage } from "@/dashboard/sales_analysis/retailer_mappings"
import { useAsyncEffect } from "@/utils"
import { api_fetch } from "src/api/client.tsx"
import { User } from "src/api/user.tsx"
import { Login } from "src/auth/Login.tsx"
import { Logout } from "src/auth/Logout.tsx"
import { Signup } from "src/auth/Signup.tsx"
import { ForgotPassword } from "src/auth/ForgotPassword.tsx"
import { ResetPassword } from "src/auth/ResetPassword.tsx"
import { SetPassword } from "src/auth/SetPassword.tsx"
import { clearUser, setUser, UserState } from "src/auth/user.tsx"
import { DeductionTable } from "@/deduction/page.tsx"
import { WonPendingPaymentPage } from "@/deduction/filtered_list_page.tsx"
import { SelectOrg } from "src/auth/SelectOrg.tsx"
import { sanitize_next_for_redirect } from "src/auth/sanitize_next.ts"
import { Landing } from "src/landing/landing.tsx"
import { SettingsConnectionsPage } from "src/settings/connections.tsx"
import { SettingsUsers } from "src/settings/users.tsx"
import { SettingsAccounting } from "src/settings/accounting.tsx"
import { SettingsCategoriesPage } from "src/settings/categories/index.tsx"
import { SettingsCodingRulesPage } from "src/settings/coding_rules/index.tsx"
import { SettingsOrganization } from "src/settings/organization.tsx"
import { SettingsApiKeys } from "src/settings/api_keys.tsx"
import { SettingsAgentPage } from "src/settings/agent.tsx"
import { SettingsPreferences } from "src/settings/preferences.tsx"
import { ActivityFeed } from "@/components/activity-feed"
import { SetupComplete } from "src/setup/complete.tsx"
import { InvitesSetup } from "src/setup/invites.tsx"
import { AccountingTransaction } from "@/accounting/accounting_transaction.tsx"
import { loadAccountCodes } from "@/accounting/accounts"
import { loadOrgSettings } from "@/org_settings/state.tsx"
import { InvitePage } from "@/setup/invite/page.tsx"
import { ConnectMailboxPage } from "@/connect_mailbox/page.tsx"
import { Transactions } from "@/transactions/page.tsx"
import { DeductionDetail } from "@/deduction/[id]/page.tsx"
import { DeductionTransactions } from "@/transactions/deductions/page.tsx"
import { SettingsConnectPage, SetupConnectPage } from "@/setup/connect/page.tsx"
import { ConnectKehePage } from "@/setup/connect/kehe/page.tsx"
import { ConnectKeheFreshdeskPage } from "@/setup/connect/kehe/freshdesk-page.tsx"
import { ConfigKehePage } from "@/setup/config/kehe/page.tsx"
import { ConnectUnfiPage } from "@/setup/connect/unfi/page.tsx"
import { ConfigUnfiPage } from "@/setup/config/unfi/page.tsx"
import { ConfigRainforestPage } from "@/setup/config/rainforest/page.tsx"
import { ConfigCrownPacificFineFoodsPage } from "@/setup/config/crownpacificfinefoods/page.tsx"
import { NewConnectionSetupPage } from "@/setup/connect/new/page.tsx"
import { ConnectAholdPage } from "@/setup/connect/ahold/page.tsx"
import { ConnectCandsPage } from "@/setup/connect/cands/page.tsx"
import { ConnectCandsDisputePortalPage } from "@/setup/connect/cands/dispute-portal-page.tsx"
import { ConnectSvHarborPage } from "@/setup/connect/sv-harbor/page.tsx"
import { ConnectSageIntacctPage } from "@/setup/connect/sage/page.tsx"
import { ConnectNetsuitePage } from "@/setup/connect/netsuite/page.tsx"
import { ConnectQuickbooksPage } from "@/setup/connect/quickbooks/page.tsx"
import { ConnectXeroPage } from "@/setup/connect/xero/page.tsx"
import { XeroSelectOrganizationPage } from "@/setup/connect/xero/select-organization/page.tsx"
import { ConnectTargetPage } from "@/setup/connect/target/page.tsx"
import { ConnectAmazonPage } from "@/setup/connect/amazon/page.tsx"
import { ConnectWalmartPage } from "@/setup/connect/walmart/page.tsx"
import { ConnectWalmartHighRadiusPage } from "@/setup/connect/walmart-highradius/page.tsx"
import { ConnectSpinsPage } from "@/setup/connect/spins/page.tsx"
import { ConnectHyVeePage } from "@/setup/connect/hyvee/page.tsx"
import { ConnectKrogerPage } from "@/setup/connect/kroger/page.tsx"
import { ConnectBozzutosPage } from "@/setup/connect/bozzutos/page.tsx"
import { ConnectDotFoodsPage } from "@/setup/connect/dotfoods/page"
import { ConnectSpsPage } from "@/setup/connect/sps/page.tsx"
import { ConnectStordPage } from "@/setup/connect/stord/page.tsx"
import { ConnectCrownPacificFineFoodsPage } from "@/setup/connect/crownpacificfinefoods/page.tsx"
import { ConnectRainforestPage } from "@/setup/connect/rainforest/page.tsx"
import { ConnectChexFinerFoodsPage } from "@/setup/connect/chexfinerfoods/page.tsx"
import { ConfigChexFinerFoodsPage } from "@/setup/config/chexfinerfoods/page.tsx"
import { ConnectGoogleDrivePage } from "@/setup/connect/googledrive/page.tsx"
import { ConnectWakefernPage } from "@/setup/connect/wakefern/page.tsx"
import { ConnectAwgPage } from "@/setup/connect/awg/page.tsx"
import { ConnectRoadtexPage } from "@/setup/connect/roadtex/page.tsx"
import { ConnectVividlyPage } from "@/setup/connect/vividly/page.tsx"
import { ConfigWakefernPage } from "@/setup/config/wakefern/page.tsx"
import { ConnectHebPage } from "@/setup/connect/heb/page.tsx"
import { ConnectAlbertsonsPage } from "@/setup/connect/albertsons/page.tsx"
import { ConnectTonysFinefooodsPage } from "@/setup/connect/tonysfinefoods/page.tsx"
import { ConnectBolEmailSyncPage } from "@/setup/connect/bol-email-sync/page.tsx"
import { ConnectGfiPage } from "@/setup/connect/gfi/page.tsx"
import { ConnectThrivePage } from "@/setup/connect/thrive/page.tsx"
import { ConnectSouthernGlazerPage } from "@/setup/connect/southern-glazer/page.tsx"
import { ConnectVitaminShoppePage } from "@/setup/connect/vitamin-shoppe/page.tsx"
import { ConnectWincoPage } from "@/setup/connect/winco/page.tsx"
import { ConnectMuscleFoodsPage } from "@/setup/connect/muscle-foods/page.tsx"
import { ConnectCostcoPage } from "@/setup/connect/costco/page.tsx"
import { ConnectEmailRemittancePage } from "@/setup/connect/email-remittance/page.tsx"
import { ConnectCoreMarkPage } from "@/setup/connect/core-mark/page.tsx"
import { ConnectBillPage } from "@/setup/connect/bill/page.tsx"
import { FileImportsPage } from "@/file_imports/page.tsx"
import { HelpPage } from "@/help/page.tsx"
import { FileImportDetailPage } from "@/file_imports/[id]/page.tsx"
import { IntegrationSettingsPage, IntegrationSettingsByIdPage } from "@/settings/integrations/page.tsx"
import { UnfiSettingsPage } from "@/settings/integrations/unfi/page.tsx"
import { KeheSettingsPage } from "@/settings/integrations/kehe/page.tsx"
import { CandsSettingsPage } from "@/settings/integrations/cands/page.tsx"
import { KrogerSettingsPage } from "@/settings/integrations/kroger/page.tsx"
import { WakefernSettingsPage } from "@/settings/integrations/wakefern/page.tsx"
import {
  SettingsConnectChexFinerFoodsPage,
  SettingsConfigChexFinerFoodsPage,
} from "@/settings/integrations/chexfinerfoods/page.tsx"
import {
  SettingsConnectRainforestPage,
  SettingsConfigRainforestPage,
} from "@/settings/integrations/rainforest/page.tsx"
import {
  SettingsConnectCrownPacificFineFoodsPage,
  SettingsConfigCrownPacificFineFoodsPage,
} from "@/settings/integrations/crownpacificfinefoods/page.tsx"
import {
  SettingsConnectKehePage,
  SettingsConnectKeheFreshdeskPage,
  SettingsConnectAholdPage,
  SettingsConnectCandsPage,
  SettingsConnectCandsDisputePortalPage,
  SettingsConnectSvHarborPage,
  SettingsConnectAmazonPage,
  SettingsConnectWalmartPage,
  SettingsConnectWalmartHighRadiusPage,
  SettingsConnectSpinsPage,
  SettingsConnectHyVeePage,
  SettingsConnectKrogerPage,
  SettingsConnectBozzutosPage,
  SettingsConnectDotFoodsPage,
  SettingsConnectSpsPage,
  SettingsConnectStordPage,
  SettingsConnectTargetPage,
  SettingsConnectWakefernPage,
  SettingsConnectAwgPage,
  SettingsConnectRoadtexPage,
  SettingsConnectVividlyPage,
  SettingsConnectHebPage,
  SettingsConnectTonysFinefooodsPage,
  SettingsConnectUnfiPage,
  SettingsNewConnectionPage,
} from "@/settings/integrations/credential-pages.tsx"
import {
  SettingsConnectQuickbooksPage,
  SettingsConnectXeroPage,
  SettingsConnectSageIntacctPage,
  SettingsConnectNetsuitePage,
  SettingsConnectGoogleDrivePage,
  SettingsConnectBolEmailSyncPage,
  SettingsConnectGfiPage,
  SettingsConnectThrivePage,
  SettingsConnectSouthernGlazerPage,
  SettingsConnectVitaminShoppePage,
  SettingsConnectWincoPage,
  SettingsConnectMuscleFoodsPage,
  SettingsConnectCostcoPage,
  SettingsConnectEmailRemittancePage,
  SettingsConnectCoreMarkPage,
  SettingsConnectAlbertsonsPage,
  SettingsConnectBillPage,
} from "@/settings/integrations/oauth-pages.tsx"
import { DeductionDetailModal } from "@/deduction_detail/DeductionDetailModal.tsx"
import { AccountingTransactionModal } from "@/accounting/AccountingTransactionModal.tsx"
import { WizardPage } from "@/wizard/page.tsx"
import { ContractDetailPage } from "@/settings/contracts/[id]/page.tsx"
import { BlogIndexPage } from "@/blog/BlogIndexPage.tsx"
import { BlogPostPage } from "@/blog/BlogPostPage.tsx"
import { SyncCanonicalLink } from "@/seo/sync_canonical.tsx"

export const DEFAULT_LOGGED_IN_PATH = "/deductions"

interface AuthenticatedProps {
  user: User | null
  children: ComponentChildren
}

export function Authenticated({ children, user }: AuthenticatedProps): VNode {
  const [loading, setLoading] = useState(true)

  useAsyncEffect(async () => {
    let res = await api_fetch<User>("/auth/me")
    if (!res.ok) {
      clearUser()
      setLoading(false)
      return
    }
    let u = res.value.data
    if (u.id != user?.id || u.org_id != user?.org_id) {
      setUser(u)
    }
    if (!u.org_id && u.role === "system_admin") {
      const loc = window.location.pathname + window.location.search
      const next = sanitize_next_for_redirect(loc) ?? DEFAULT_LOGGED_IN_PATH
      const r = await api_fetch<{ resolved: boolean }>("/auth/select-org-for-next", {
        body: { next },
      })
      if (r.ok && r.value.data.resolved) {
        const me = await api_fetch<User>("/auth/me")
        if (me.ok) {
          setUser(me.value.data)
        }
      }
    }
    setLoading(false)
  }, [])

  useAsyncEffect(() => (user?.org_id ? loadAccountCodes() : Promise.resolve()), [user?.org_id])
  useAsyncEffect(() => (user?.org_id ? loadOrgSettings() : Promise.resolve()), [user?.org_id])

  if (loading) {
    return <div>Loading...</div>
  }
  if (!user) {
    let next = window.location.pathname + window.location.search
    next = "/login?" + new URLSearchParams({ next }).toString()
    return <Redirect to={next} />
  }
  if (!user.org_id) {
    const ret = sanitize_next_for_redirect(window.location.pathname + window.location.search)
    const to = ret ? `/select-org?next=${encodeURIComponent(ret)}` : "/select-org"
    return <Redirect to={to} />
  }
  return children as VNode
}

export function Routes() {
  const user = UserState.use()
  const [location] = useLocation()

  // Reset title on every navigation - individual pages can override with usePageTitle
  // useLayoutEffect so this fires before child useEffect hooks, allowing pages to set their own title
  useLayoutEffect(() => {
    document.title = "MarginWiz"
  }, [location])

  return (
    <Fragment>
      <SyncCanonicalLink />
      <Switch>
        <Route path="/">{() => (user ? <Redirect to={DEFAULT_LOGGED_IN_PATH} /> : <Landing />)}</Route>
        <Route path="/invite/:commonName" component={InvitePage} />
        {/* Public: opened by whoever controls a mailbox (usually the brand's IT admin), who has no MarginWiz login. */}
        <Route path="/connect-mailbox/:id" component={ConnectMailboxPage} />
        <Route path="/login" component={Login} />
        <Route path="/signup" component={Signup} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/set-password" component={SetPassword} />
        <Route path="/logout" component={Logout} />
        <Route path="/select-org" component={SelectOrg} />

        <Route path="/blog/:slug" component={BlogPostPage} />
        <Route path="/blog" component={BlogIndexPage} />

        <Authenticated user={user}>
          <DeductionDetailModal />
          <AccountingTransactionModal />
          <Switch>
            <Route path="/setup/invites" component={InvitesSetup} />
            <Route path="/setup/complete" component={SetupComplete} />

            <Route path="/setup/connect" component={SetupConnectPage} />
            <Route path="/setup/connect/kehe" component={ConnectKehePage} />
            <Route path="/setup/connect/kehe-freshdesk" component={ConnectKeheFreshdeskPage} />
            <Route path="/setup/config/kehe" component={ConfigKehePage} />
            <Route path="/setup/connect/unfi" component={ConnectUnfiPage} />
            <Route path="/setup/config/unfi" component={ConfigUnfiPage} />
            <Route path="/setup/connect/new" component={NewConnectionSetupPage} />
            <Route path="/setup/connect/ahold" component={ConnectAholdPage} />
            <Route path="/setup/connect/cands" component={ConnectCandsPage} />
            <Route path="/setup/connect/cands-dispute-portal" component={ConnectCandsDisputePortalPage} />
            <Route path="/setup/connect/svharbor" component={ConnectSvHarborPage} />
            <Route path="/setup/connect/sageintacct" component={ConnectSageIntacctPage} />
            <Route path="/setup/connect/netsuite" component={() => <ConnectNetsuitePage />} />
            <Route path="/setup/connect/quickbooks" component={ConnectQuickbooksPage} />
            <Route path="/setup/connect/xero" component={ConnectXeroPage} />
            <Route path="/setup/connect/xero/select-organization" component={XeroSelectOrganizationPage} />
            <Route path="/setup/connect/target" component={ConnectTargetPage} />
            <Route path="/setup/connect/amazon" component={ConnectAmazonPage} />
            <Route path="/setup/connect/walmart" component={ConnectWalmartPage} />
            <Route path="/setup/connect/walmart-highradius" component={ConnectWalmartHighRadiusPage} />
            <Route path="/setup/connect/spins" component={ConnectSpinsPage} />
            <Route path="/setup/connect/hyvee" component={ConnectHyVeePage} />
            <Route path="/setup/connect/kroger" component={ConnectKrogerPage} />
            <Route path="/setup/connect/bozzutos" component={ConnectBozzutosPage} />
            <Route path="/setup/connect/dotfoods" component={ConnectDotFoodsPage} />
            <Route path="/setup/connect/sps" component={ConnectSpsPage} />
            <Route path="/setup/connect/stord" component={ConnectStordPage} />
            <Route path="/setup/connect/crownpacificfinefoods" component={ConnectCrownPacificFineFoodsPage} />
            <Route path="/setup/config/crownpacificfinefoods" component={ConfigCrownPacificFineFoodsPage} />
            <Route path="/setup/connect/rainforest" component={ConnectRainforestPage} />
            <Route path="/setup/config/rainforest" component={ConfigRainforestPage} />
            <Route path="/setup/connect/chexfinerfoods" component={ConnectChexFinerFoodsPage} />
            <Route path="/setup/config/chexfinerfoods" component={ConfigChexFinerFoodsPage} />
            <Route path="/setup/connect/googledrive" component={ConnectGoogleDrivePage} />
            <Route path="/setup/connect/wakefern" component={ConnectWakefernPage} />
            <Route path="/setup/connect/awg" component={ConnectAwgPage} />
            <Route path="/setup/connect/roadtex" component={ConnectRoadtexPage} />
            <Route path="/setup/connect/vividly" component={ConnectVividlyPage} />
            <Route path="/setup/config/wakefern" component={ConfigWakefernPage} />
            <Route path="/setup/connect/heb" component={ConnectHebPage} />
            <Route path="/setup/connect/albertsons" component={ConnectAlbertsonsPage} />
            <Route path="/setup/connect/tonysfinefoods" component={ConnectTonysFinefooodsPage} />
            <Route path="/setup/connect/bol-email-sync" component={ConnectBolEmailSyncPage} />
            <Route path="/setup/connect/gfi" component={ConnectGfiPage} />
            <Route path="/setup/connect/thrive" component={ConnectThrivePage} />
            <Route path="/setup/connect/southern-glazer" component={ConnectSouthernGlazerPage} />
            <Route path="/setup/connect/vitamin-shoppe" component={ConnectVitaminShoppePage} />
            <Route path="/setup/connect/winco" component={ConnectWincoPage} />
            <Route path="/setup/connect/muscle-foods" component={ConnectMuscleFoodsPage} />
            <Route path="/setup/connect/costco" component={ConnectCostcoPage} />
            <Route path="/setup/connect/email-remittance" component={ConnectEmailRemittancePage} />
            <Route path="/setup/connect/core-mark" component={ConnectCoreMarkPage} />
            <Route path="/setup/connect/bill" component={ConnectBillPage} />

            {/* Default route for all non-setup pages - must be last in Switch */}
            <Route>
              <GatedContainer>
                <Route path="/wizard" component={WizardPage} />
                <Route path="/dashboard" component={Sales} />
                <Route path="/trade-promotion-management/accrual-report" component={TradePromotionManagement} />
                <Route path="/trade-promotion-management/accrual-bank" component={AccrualBankPage} />
                <Route path="/trade-promotion-management/trade-review" component={TradeReviewPage} />
                <Route path="/trade-promotion-management/promo-lift-review" component={PromoLiftReviewPage} />
                <Route path="/trade-promotion-management/trade-rate" component={TradePromotionManagement} />
                <Route path="/trade-promotion-management/accruals" component={TradePromotionManagement} />
                <Route path="/trade-promotion-management/create" component={CreatePromotionPage} />
                <Route path="/trade-promotion-management" component={TradePromotionManagement} />
                <Route path="/deductions" component={DeductionTable} />
                <Route path="/deductions/won-pending-payment" component={WonPendingPaymentPage} />
                <Route path="/deduction/:deduction_id" component={DeductionDetail} />
                <Route path="/transactions" component={Transactions} />
                {/*TODO rename to /transaction/:id */}
                <Route path="/accounting/transaction/:id" component={AccountingTransaction} />
                <Route path="/transactions/deductions" component={DeductionTransactions} />
                <Route path="/accounting/checks" component={AccountingChecks} />
                <Route path="/accounting/check/new" component={NewCheck} />
                <Route path="/accounting/check/:checkNumber" component={CheckDetail} />
                <Route path="/accounting/check/:checkNumber/apply" component={CashApplicationConfirmPage} />
                <Route path="/accounting/short-pays" component={ShortPaysPage} />
                <Route path="/netsuite/:id" component={NetsuiteDocumentPage} />
                <Route path="/fill-rate" component={FillRatePage} />
                <Route path="/spoilage" component={SpoilagePage} />
                <Route path="/deliveries" component={DeliveriesPage} />
                <Route path="/purchase-orders" component={PurchaseOrdersPage} />
                <Route path="/accounting/open-deductions" component={OpenDeductionsPage} />
                <Route path="/credit-memo/:id" component={CreditMemoDetailPage} />
                <Route path="/activities" component={() => <ActivityFeed isStandalone />} />
                <Route path="/file-imports" component={FileImportsPage} />
                <Route path="/help" component={HelpPage} />
                <Route path="/help/:slug" component={HelpPage} />
                <Route path="/file-imports/:id" component={FileImportDetailPage} />
                <Route path="/sales-analysis/retailer-mappings" component={RetailerMappingsPage} />
                <Route path="/sales-analysis" component={SalesAnalysis} />
                <Route path="/sales-forecasting" component={SalesForecasting} />
                <Route path="/sales-forecasting/mappings" component={ForecastMappingsPage} />
                <Route path="/settings/connections" component={SettingsConnectionsPage} />
                <Route path="/settings/connect" component={SettingsConnectPage} />

                {/* Settings connect routes - dedicated pages with back button */}
                <Route path="/settings/connect/kehe" component={SettingsConnectKehePage} />
                <Route path="/settings/connect/kehe-freshdesk" component={SettingsConnectKeheFreshdeskPage} />
                <Route path="/settings/connect/unfi" component={SettingsConnectUnfiPage} />
                <Route path="/settings/connect/new" component={SettingsNewConnectionPage} />
                <Route path="/settings/connect/ahold" component={SettingsConnectAholdPage} />
                <Route path="/settings/connect/cands" component={SettingsConnectCandsPage} />
                <Route
                  path="/settings/connect/cands-dispute-portal"
                  component={SettingsConnectCandsDisputePortalPage}
                />
                <Route path="/settings/connect/svharbor" component={SettingsConnectSvHarborPage} />
                <Route path="/settings/connect/sageintacct" component={SettingsConnectSageIntacctPage} />
                <Route path="/settings/connect/netsuite" component={SettingsConnectNetsuitePage} />
                <Route path="/settings/connect/quickbooks" component={SettingsConnectQuickbooksPage} />
                <Route path="/settings/connect/xero" component={SettingsConnectXeroPage} />
                <Route path="/settings/connect/target" component={SettingsConnectTargetPage} />
                <Route path="/settings/connect/amazon" component={SettingsConnectAmazonPage} />
                <Route path="/settings/connect/walmart" component={SettingsConnectWalmartPage} />
                <Route path="/settings/connect/walmart-highradius" component={SettingsConnectWalmartHighRadiusPage} />
                <Route path="/settings/connect/spins" component={SettingsConnectSpinsPage} />
                <Route path="/settings/connect/hyvee" component={SettingsConnectHyVeePage} />
                <Route path="/settings/connect/kroger" component={SettingsConnectKrogerPage} />
                <Route path="/settings/connect/bozzutos" component={SettingsConnectBozzutosPage} />
                <Route path="/settings/connect/dotfoods" component={SettingsConnectDotFoodsPage} />
                <Route path="/settings/connect/sps" component={SettingsConnectSpsPage} />
                <Route path="/settings/connect/stord" component={SettingsConnectStordPage} />
                <Route
                  path="/settings/connect/crownpacificfinefoods"
                  component={SettingsConnectCrownPacificFineFoodsPage}
                />
                <Route path="/settings/connect/rainforest" component={SettingsConnectRainforestPage} />
                <Route path="/settings/connect/googledrive" component={SettingsConnectGoogleDrivePage} />
                <Route path="/settings/connect/bol-email-sync" component={SettingsConnectBolEmailSyncPage} />
                <Route path="/settings/connect/gfi" component={SettingsConnectGfiPage} />
                <Route path="/settings/connect/thrive" component={SettingsConnectThrivePage} />
                <Route path="/settings/connect/southern-glazer" component={SettingsConnectSouthernGlazerPage} />
                <Route path="/settings/connect/vitamin-shoppe" component={SettingsConnectVitaminShoppePage} />
                <Route path="/settings/connect/winco" component={SettingsConnectWincoPage} />
                <Route path="/settings/connect/muscle-foods" component={SettingsConnectMuscleFoodsPage} />
                <Route path="/settings/connect/costco" component={SettingsConnectCostcoPage} />
                <Route path="/settings/connect/email-remittance" component={SettingsConnectEmailRemittancePage} />
                <Route path="/settings/connect/core-mark" component={SettingsConnectCoreMarkPage} />
                <Route path="/settings/connect/bill" component={SettingsConnectBillPage} />
                <Route path="/settings/connect/wakefern" component={SettingsConnectWakefernPage} />
                <Route path="/settings/connect/awg" component={SettingsConnectAwgPage} />
                <Route path="/settings/connect/roadtex" component={SettingsConnectRoadtexPage} />
                <Route path="/settings/connect/vividly" component={SettingsConnectVividlyPage} />
                <Route path="/settings/connect/heb" component={SettingsConnectHebPage} />
                <Route path="/settings/connect/albertsons" component={SettingsConnectAlbertsonsPage} />
                <Route path="/settings/connect/tonysfinefoods" component={SettingsConnectTonysFinefooodsPage} />
                <Route path="/settings/connect/chexfinerfoods" component={SettingsConnectChexFinerFoodsPage} />

                {/* Settings config routes */}
                <Route path="/settings/config/unfi" component={UnfiSettingsPage} />
                <Route path="/settings/config/kehe" component={KeheSettingsPage} />
                <Route path="/settings/config/cands" component={CandsSettingsPage} />
                <Route path="/settings/config/kroger" component={KrogerSettingsPage} />
                <Route path="/settings/config/wakefern" component={WakefernSettingsPage} />
                <Route path="/settings/config/chexfinerfoods" component={SettingsConfigChexFinerFoodsPage} />
                <Route path="/settings/config/rainforest" component={SettingsConfigRainforestPage} />
                <Route
                  path="/settings/config/crownpacificfinefoods"
                  component={SettingsConfigCrownPacificFineFoodsPage}
                />
                <Route path="/settings/config/id/:id" component={IntegrationSettingsByIdPage} />
                <Route path="/settings/config/:name" component={IntegrationSettingsPage} />
                <Route path="/settings/contracts/:id">{() => <ContractDetailPage />}</Route>
                <Route path="/settings/users" component={SettingsUsers} />
                <Route path="/settings/accounting" component={SettingsAccounting} />
                <Route path="/settings/categories" component={SettingsCategoriesPage} />
                <Route path="/settings/coding-rules" component={SettingsCodingRulesPage} />
                <Route path="/settings/organization" component={SettingsOrganization} />
                <Route path="/settings/api-keys" component={SettingsApiKeys} />
                <Route path="/settings/agent" component={SettingsAgentPage} />
                <Route path="/settings/preferences" component={SettingsPreferences} />
                <Route path="/settings">
                  {() => {
                    window.location.href = "/settings/connections"
                    return null
                  }}
                </Route>
              </GatedContainer>
            </Route>
          </Switch>
        </Authenticated>
      </Switch>
    </Fragment>
  )
}
